import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  categoryName: string;
  author: string;
  publishedAt: string;
  tags: string[];
  isHot?: boolean;
  isNew?: boolean;
  viewCount: number;
  commentCount: number;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  coverImage,
  category,
  categoryName,
  author,
  publishedAt,
  tags,
  isHot,
  isNew,
  viewCount,
  commentCount,
  variant = 'default',
}: ArticleCardProps) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return '刚刚';
    if (hours < 24) return `${hours}小时前`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const formatCount = (count: number) => {
    if (count >= 10000) return `${(count / 10000).toFixed(1)}万`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  const isFeatured = variant === 'featured';
  const isCompact = variant === 'compact';

  return (
    <Link
      to={`/article/${slug}`}
      className={`group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 ${
        isFeatured ? '' : ''
      }`}
    >
      <div className={`${isFeatured ? 'flex flex-col lg:flex-row' : 'flex flex-col'}`}>
        <div
          className={`relative overflow-hidden ${
            isFeatured
              ? 'w-full lg:w-[480px] shrink-0 h-48 lg:h-64'
              : isCompact
              ? 'h-40'
              : 'h-48'
          }`}
        >
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {(isHot || isNew) && (
              <span
                className={`px-2 py-0.5 text-xs font-semibold rounded-full text-background-50 ${
                  isHot ? 'bg-accent-500' : 'bg-primary-500'
                }`}
              >
                {isHot ? 'HOT' : 'NEW'}
              </span>
            )}
          </div>
        </div>

        <div className={`flex flex-col gap-2 ${isFeatured ? 'p-4 md:p-5 flex-1 justify-center' : 'p-4'}`}>
          <div className="flex items-center gap-3">
            <Link
              to={`/category/${category}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-medium text-primary-500 hover:text-primary-600 px-2 py-0.5 bg-primary-50 rounded-full whitespace-nowrap"
            >
              {categoryName}
            </Link>
            <span className="text-xs text-foreground-500">{formatDate(publishedAt)}</span>
          </div>

          <h3
            className={`font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 ${
              isFeatured ? 'text-lg md:text-xl' : 'text-base'
            }`}
          >
            {title}
          </h3>

          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {excerpt}
          </p>

          <div className="flex items-center justify-between mt-auto pt-2">
            <div className="flex items-center gap-1 flex-wrap">
              {tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded"
                >
                  #{tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatCount(viewCount)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {formatCount(commentCount)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}